import { Mutation } from "@apollo/client/react/components";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import Link from "next/link";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import LeaveGroupAsGovernment from "~/lib/mutations/LeaveGroupAsGovernment";
import {
  ILeaveGroupAsGovernmentMutation,
  ILeaveGroupAsGovernmentMutationVariables,
} from "~/lib/mutations/__generated__/LeaveGroupAsGovernment.generated";
import { IGovernmentQuery } from "~/lib/queries/__generated__/GovernmentQuery.generated";
import { IGovernment } from "~/types/types";

interface IGovernmentGroupsProps {
  government: Pick<IGovernment, "_id" | "name"> & {
    groups: NonNullable<IGovernmentQuery["government"]>["groups"];
  };
}

export default function GovernmentGroups({ government }: IGovernmentGroupsProps) {
  const intl = useIntl();
  const { groups } = government;

  return (
    <div
      css={{
        backgroundColor: palette.white,
        borderTop: `solid 1px ${palette.lightestGray}`,
        position: "relative",
      }}
    >
      <div
        css={{
          padding: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          css={{
            textTransform: "uppercase",
            fontSize: 12,
            color: palette.sealBlue,
            letterSpacing: 0.5,
            fontWeight: 500,
          }}
        >
          <FormattedMessage defaultMessage="Groups" id="hzmswI" />
        </span>
      </div>
      <div
        css={{
          padding: 16,
          paddingTop: 0,
        }}
      >
        <Mutation<ILeaveGroupAsGovernmentMutation, ILeaveGroupAsGovernmentMutationVariables>
          mutation={LeaveGroupAsGovernment}
        >
          {(leaveGroup, { loading }) => (
            <div
              css={{
                display: "grid",
                gridRowGap: 16,
              }}
            >
              {groups.map((group) => (
                <Link key={group._id} passHref={true} href="/groups/[groupSlug]" as={`/groups/${group.slug}`}>
                  <a
                    css={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <div
                      css={{
                        position: "relative",
                      }}
                    >
                      <div
                        role="button"
                        css={{
                          cursor: "pointer",
                          position: "absolute",
                          top: -5,
                          zIndex: 10,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "100%",
                          right: -5,
                          width: 16,
                          height: 16,
                          background: palette.red,
                        }}
                        onClick={(event) => {
                          event.preventDefault();

                          if (!loading) {
                            const message = intl.formatMessage(
                              {
                                defaultMessage: 'Are you sure you want to remove this group for {governmentName}?', id: 'wfVQdU',
                              },
                              {
                                governmentName: government.name,
                              },
                            );

                            if (window.confirm(message)) {
                              leaveGroup({
                                variables: {
                                  groupId: group._id,
                                },
                              });
                            }
                          }
                        }}
                      >
                        <svg width={10} height={10} viewBox="0 0 10 10">
                          <path
                            fill="#FFF"
                            d="M6.188 5l3.566-3.565A.84.84 0 1 0 8.565.246L5 3.812 1.435.246A.84.84 0 1 0 .246 1.435L3.812 5 .246 8.565a.84.84 0 0 0 1.189 1.189L5 6.188l3.565 3.566a.84.84 0 1 0 1.189-1.189L6.188 5z"
                          />
                        </svg>
                      </div>

                      <ProfilePicture image={group.logo} name={group.name} size={40} />
                    </div>

                    <span
                      css={{
                        color: palette.darkGray,
                        marginLeft: 8,
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {group.name}
                    </span>
                  </a>
                </Link>
              ))}
            </div>
          )}
        </Mutation>
      </div>
    </div>
  );
}
